import { Button, Icon, Typography, VerticalDivider } from '@smartgift/frontend-toolkit';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useNavigate } from 'react-router-dom';

import {
  HOME_RECOGNITIONS_TABLE_LIMIT,
  RECOGNITION_CREATION_STEPS,
  RECOGNITION_STATUSES
} from '@hero/constants';
import { useRecognitionCreateModal } from '@hero/hooks';
import { OrderStatusBadge, Table } from '@hero/molecules';
import { TableProducts } from '@hero/organisms';
import { ROUTE_PATHS } from '@hero/routes';
import { useGetRecognitions } from '@hero/services/order-service/recognition/hooks';
import { useNewRecognitionStore } from '@hero/store/hooks';
import { formatDate } from '@hero/utils/helper';

import * as S from './style';

function RecentRecognitionsTable() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showRecognitionCreateModal } = useRecognitionCreateModal();
  const draftStatuses = [
    RECOGNITION_STATUSES.draft,
    RECOGNITION_STATUSES.pending_approval,
    RECOGNITION_STATUSES.approved
  ];

  const isRecognitionStatusDraft = original => draftStatuses.includes(original?.status?.current);

  const [globalFilters, setGlobalFilters] = useState({});
  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: HOME_RECOGNITIONS_TABLE_LIMIT
  });

  const { data: myRecognitions, isLoading: isLoadingRecognitions } = useGetRecognitions({
    filters: {
      searchTerm: globalFilters.searchTerm
    },
    pagination: {
      page: pageIndex + 1,
      limit: pageSize
    }
  });

  const {
    updateProducts,
    updateContacts,
    updateExperienceSettings,
    updateMeta,
    updateCode,
    updateName,
    updateSettings
  } = useNewRecognitionStore();

  const renderProducts = ({ row }) => {
    return <TableProducts products={row.original?.products || []} maxVisibleProducts={2} />;
  };

  const onNavigateToDetailPage = original => {
    if (isRecognitionStatusDraft(original)) {
      if (original?.meta?.creationStep !== 'NEW_RECOGNITION_REVIEW_PAY') {
        updateCode(original.code);
        updateProducts(original.products);
        updateName(original.name);
        updateContacts(original.contacts);
        updateExperienceSettings(original.experienceSettings);
        updateMeta(original.meta);
        updateSettings(original.settings);

        navigate(ROUTE_PATHS[original?.meta?.creationStep]);
      } else {
        navigate(ROUTE_PATHS.NEW_RECOGNITION_REVIEW_PAY(original.code));
      }
    } else {
      navigate({
        pathname: ROUTE_PATHS.MY_RECOGNITION_DETAIL(original.code),
        search: `?${createSearchParams({
          status: original?.status?.current
        })}`
      });
    }
  };

  const columns = React.useMemo(
    () => [
      {
        id: 'name',
        header: t('name'),
        accessorKey: 'name',
        size: 150,
        cell: ({ cell: { row } }) => {
          return (
            <Typography
              variant="body2"
              as="p"
              lineClamp={1}
              onClick={() => onNavigateToDetailPage(row.original)}
              underline
              style={{ cursor: 'pointer' }}
              title={row.original.name}
            >
              {row.original.name}
            </Typography>
          );
        }
      },
      {
        header: t('gifts'),
        accessor: 'products',
        cell: renderProducts,
        screen: ['desktop']
      },
      {
        id: 'contact',
        header: `#${t('recipients')}`,
        accessorKey: 'numberOfContacts',
        sortType: 'number',
        screen: ['desktop']
      },
      {
        id: 'expires-date',
        header: t('endDate'),
        cell: ({ cell: { row } }) => {
          return formatDate(row.original?.settings?.expiresAt, {
            format: 'DD MMM YYYY',
            includeTime: false
          });
        },
        screen: ['tablet', 'desktop']
      },
      {
        header: t('status'),
        accessor: 'status',
        cell: ({ cell: { row } }) => {
          const data = row?.original;
          const currentStatus = data?.status.current;
          const isReadyToSend =
            currentStatus === RECOGNITION_STATUSES.draft &&
            data?.meta?.creationStep === RECOGNITION_CREATION_STEPS.newRecognitionReviewPay;

          return (
            <OrderStatusBadge
              status={isReadyToSend ? RECOGNITION_STATUSES.readyToSend : currentStatus}
            />
          );
        }
      }
    ],
    []
  );

  return (
    <S.Recognition>
      <S.Header>
        <Icon name="gift-box" size="lg" />
        <Typography variant="body1Bold">{t('recentRecognitions')}</Typography>
        <VerticalDivider $height="16px" $margin="0 12px" />
        <Button variant="link" onClick={() => navigate(ROUTE_PATHS.MY_RECOGNITIONS)}>
          {t('seeAll')}
        </Button>
      </S.Header>
      <Table
        columns={columns}
        data={myRecognitions?.docs || []}
        globalFilters={globalFilters}
        pagination={{
          pageIndex,
          pageSize,
          total: myRecognitions?.pagination?.total
        }}
        manualPagination
        loading={isLoadingRecognitions}
        setGlobalFilters={setGlobalFilters}
        setPagination={setPagination}
        pageCount={myRecognitions?.pagination?.pages}
        emptyTableContent={{
          createNew: showRecognitionCreateModal,
          feature: 'recognition',
          icon: 'gift-box'
        }}
      />
    </S.Recognition>
  );
}

export { RecentRecognitionsTable };
